/**
 * Feature 100 — /partner-trial request form.
 * Guest submission (reCAPTCHA guest session), on-page confirmation only — no
 * account is created and no email is sent to the prospect.
 */

import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useRecaptchaGuestSession } from '@/features/guest-session/hooks/useRecaptchaGuestSession';
import { submitTrialRequest } from '../api/trialRequestApi';

type FormState = {
  firstName: string;
  lastName: string;
  businessEmail: string;
  companyName: string;
  jobTitle: string;
  phoneNumber: string;
  firmWebsite: string;
  hearAbout: string;
};

const EMPTY: FormState = {
  firstName: '',
  lastName: '',
  businessEmail: '',
  companyName: '',
  jobTitle: '',
  phoneNumber: '',
  firmWebsite: '',
  hearAbout: '',
};

const REQUIRED: Array<keyof FormState> = [
  'firstName',
  'lastName',
  'businessEmail',
  'companyName',
  'jobTitle',
  'hearAbout',
];

export function TrialRequestForm() {
  const { isAuthenticated } = useAuth();
  const { recaptchaElement, ensureGuestSession } = useRecaptchaGuestSession();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const isValid =
    REQUIRED.every((f) => form[f].trim().length > 0) && /.+@.+\..+/.test(form.businessEmail);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await ensureGuestSession(isAuthenticated);
      await submitTrialRequest({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        businessEmail: form.businessEmail.trim(),
        companyName: form.companyName.trim(),
        jobTitle: form.jobTitle.trim(),
        phoneNumber: form.phoneNumber.trim() || undefined,
        firmWebsite: form.firmWebsite.trim() || undefined,
        hearAbout: form.hearAbout.trim(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong submitting your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <Card className="p-8 text-center" data-testid="trial-request-confirmation">
        <CheckCircle2 className="w-12 h-12 text-[#026857] mx-auto mb-4" aria-hidden="true" />
        <h3 className="text-xl text-gray-900 mb-2">Request received!</h3>
        <p className="text-gray-600 mb-3">
          A member of the bQuest team will review your request and send your login information
          along with resources to help you get the most from your experience.
        </p>
        <p className="text-gray-600">
          Questions in the meantime? Reach us at{' '}
          <a href="mailto:info@thebquest.com" className="text-[#026857] font-medium hover:underline">
            info@thebquest.com
          </a>
          .
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-8">
      <form onSubmit={handleSubmit} noValidate>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="firstName">First Name *</Label>
            <Input id="firstName" value={form.firstName} onChange={set('firstName')} required />
          </div>
          <div>
            <Label htmlFor="lastName">Last Name *</Label>
            <Input id="lastName" value={form.lastName} onChange={set('lastName')} required />
          </div>
          <div>
            <Label htmlFor="businessEmail">Business Email *</Label>
            <Input
              id="businessEmail"
              type="email"
              value={form.businessEmail}
              onChange={set('businessEmail')}
              required
            />
          </div>
          <div>
            <Label htmlFor="companyName">Company/Firm Name *</Label>
            <Input id="companyName" value={form.companyName} onChange={set('companyName')} required />
          </div>
          <div>
            <Label htmlFor="jobTitle">Job Title *</Label>
            <Input id="jobTitle" value={form.jobTitle} onChange={set('jobTitle')} required />
          </div>
          <div>
            <Label htmlFor="phoneNumber">Phone Number (Optional)</Label>
            <Input id="phoneNumber" type="tel" value={form.phoneNumber} onChange={set('phoneNumber')} />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="firmWebsite">Firm Website (Optional)</Label>
            <Input id="firmWebsite" type="url" value={form.firmWebsite} onChange={set('firmWebsite')} />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="hearAbout">How did you hear about bQuest? *</Label>
            <Textarea id="hearAbout" rows={3} value={form.hearAbout} onChange={set('hearAbout')} required />
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-600 mb-4" role="alert">
            {error}
          </p>
        )}

        <Button
          type="submit"
          disabled={!isValid || submitting}
          className="w-full bg-[#026857] hover:bg-[#025247] h-12 text-base"
        >
          {submitting ? 'Submitting…' : 'Request My Trial'}
        </Button>
        {recaptchaElement}
      </form>
    </Card>
  );
}
